import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";

const expenseTypes = [
  { value: "labor", label: "Lønn" },
  { value: "materials", label: "Materialer" },
  { value: "travel", label: "Reise" },
  { value: "equipment", label: "Utstyr" },
  { value: "software", label: "Programvare" },
  { value: "consulting", label: "Konsulenter" },
  { value: "other", label: "Annet" },
];

const statusOptions = [
  { value: "planned", label: "Planlagt" },
  { value: "committed", label: "Forpliktet" },
  { value: "invoiced", label: "Fakturert" },
  { value: "paid", label: "Betalt" },
];

export default function ExpenseForm({ expense, projectId, projectName, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    project_id: projectId,
    project_name: projectName,
    expense_type: expense?.expense_type || "materials",
    category: expense?.category || "",
    description: expense?.description || "",
    estimated_amount: expense?.estimated_amount || "",
    actual_amount: expense?.actual_amount || "",
    expense_date: expense?.expense_date || "",
    invoice_number: expense?.invoice_number || "",
    status: expense?.status || "planned",
    notes: expense?.notes || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      estimated_amount: parseFloat(formData.estimated_amount) || 0,
      actual_amount: parseFloat(formData.actual_amount) || 0,
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-slate-900">
            {expense ? "Rediger Utgift" : "Ny Utgift"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expense_type">Kostnadstype *</Label>
              <Select
                value={formData.expense_type}
                onValueChange={(value) => setFormData({ ...formData, expense_type: value })}
              >
                <SelectTrigger id="expense_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {expenseTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Kategori/Beskrivelse *</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                placeholder="f.eks. Servere, Flyreise Oslo-Bergen"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Detaljert beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Ytterligere detaljer om utgiften"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="estimated_amount">Estimert beløp (NOK)</Label>
              <Input
                id="estimated_amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.estimated_amount}
                onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="actual_amount">Faktisk beløp (NOK)</Label>
              <Input
                id="actual_amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.actual_amount}
                onChange={(e) => setFormData({ ...formData, actual_amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expense_date">Utgiftsdato</Label>
              <Input
                id="expense_date"
                type="date"
                value={formData.expense_date}
                onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice_number">Fakturanummer</Label>
            <Input
              id="invoice_number"
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              placeholder="F-2024-001"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notater</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              placeholder="Interne notater"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {expense ? "Oppdater" : "Opprett"} Utgift
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}